<script setup lang="ts">
import {computed} from 'vue'
import SkeletonRectangle from '@toolify/client/src/modules/ui/components/SkeletonRectangle.vue'
import {getSeededNumberInRange} from '@toolify/client/src/modules/ui/functions/getSeededNumberInRange'
import {ChooseItemSkeletonShapeType} from '@toolify/client/src/modules/list/enum/ChooseItemSkeletonShapeType'

interface Props {
  index: number
  itemHeight: number
  shape?: ChooseItemSkeletonShapeType
  hasHandle?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  shape: ChooseItemSkeletonShapeType.Text,
  hasHandle: false,
})

const hasLeadingShape = computed(() => {
  return props.shape === ChooseItemSkeletonShapeType.Icon || props.shape === ChooseItemSkeletonShapeType.Avatar
})

const contentWidth = computed(() => {
  const [min, max] = props.shape === ChooseItemSkeletonShapeType.Pill ? [30, 55] : [40, 75]
  return `${Math.round(getSeededNumberInRange(props.index + 1, min, max))}%`
})
</script>

<template>
  <div
    class="choose-item-skeleton"
    :class="`shape-${props.shape}`"
    :style="{height: `${props.itemHeight}px`}"
  >
    <div class="choose-item-skeleton-handle" v-if="props.hasHandle">
      <SkeletonRectangle class="choose-item-skeleton-handle-bar" radius="2px"/>
    </div>
    <SkeletonRectangle
      class="choose-item-skeleton-leading"
      :radius="props.shape === ChooseItemSkeletonShapeType.Avatar ? '100rem' : '4px'"
      v-if="hasLeadingShape"
    />
    <SkeletonRectangle
      class="choose-item-skeleton-content"
      :radius="props.shape === ChooseItemSkeletonShapeType.Pill ? '100rem' : '4px'"
      :style="{width: contentWidth}"
    />
  </div>
</template>

<style scoped lang="scss">
@use '../../../styles/variables' as variables;

.choose-item-skeleton {
  display: flex;
  align-items: center;
  gap: map-get(variables.$spacing, space-2);
  width: 100%;
  padding: map-get(variables.$spacing, space-1) map-get(variables.$spacing, space-2);

  .choose-item-skeleton-handle {
    display: flex;
    justify-content: center;
    flex-shrink: 0;
    width: map-get(variables.$sizes, size-sm);
  }

  .choose-item-skeleton-handle-bar {
    width: 0.5rem;
    height: 0.875rem;
  }

  .choose-item-skeleton-leading {
    flex-shrink: 0;
    width: 1rem;
    height: 1rem;
  }

  .choose-item-skeleton-content {
    height: 0.625rem;
  }

  &.shape-icon {
    gap: map-get(variables.$spacing, space-1);
  }

  &.shape-avatar .choose-item-skeleton-leading {
    width: map-get(variables.$sizes, size-sm);
    height: map-get(variables.$sizes, size-sm);
    margin-left: map-get(variables.$spacing, space-2);
  }

  &.shape-pill .choose-item-skeleton-content {
    height: map-get(variables.$sizes, size-md);
  }
}
</style>
